import React, { useCallback, useEffect, useState } from 'react';

import {
  EstadoDaSincronizacao,
  ModoSync,
  definirModoDoSync,
  definirPausaDoSync,
  fetchEstadoDaSincronizacao,
} from '../../../api/comprasDashboardApi';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';
// A faixa de atenção (`aviso-de-tela`) mora aqui — importar explicitamente, em vez de contar
// com o CSS que outra tela do bundle carregou (o acoplamento invisível do `BUG-V74`).
import '../../../shared/feedback/aviso-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../pages/css/adminSistema.css';

/**
 * Os dois controles da sincronização com o Nasajon (at-108).
 *
 * ## Por que existem
 *
 * O sync consome a API do fornecedor cerca de 2.800 vezes por dia. O Victor pediu, em 18/09/2026,
 * um jeito de **parar 100%** quando for preciso, e um **ritmo reduzido** para períodos de pouco uso.
 *
 * ## 🔴 São dois controles independentes, não um seletor de três posições
 *
 * Decisão do Victor. Pausar é emergência **sobre** um ritmo: retomar devolve o ritmo que estava
 * gravado, sem quem retoma ter de lembrar qual era. Com um seletor único, sair da pausa obrigaria a
 * escolher de novo — e a escolha errada só apareceria como consumo, nunca como erro.
 *
 * ## Sem atualização otimista, e texto FIXO no erro
 *
 * O botão não se move até o servidor confirmar (molde do `AtivacaoDosRadaresSection`): se a tela
 * movesse o controle e a gravação falhasse, o ADMIN sairia daqui acreditando ter pausado algo que
 * continua consumindo a API. E a mensagem de erro é escrita à mão em vez de `errorFrom` porque aqui
 * a **consequência** importa mais que o motivo HTTP — o que se precisa saber é em que estado a
 * sincronização ficou (regra da at-65).
 */

/**
 * Estimativas da análise da at-108, ditas como estimativa na tela. ⚠️ A do ritmo normal é anterior
 * à transversal/at-99, que reduziu as leituras (QUENTE de 1 para 3 min) — o número de hoje é menor
 * e não foi medido, por isso a tela diz "até".
 */
const CHAMADAS_POR_DIA_NORMAL = 'até cerca de 2.800';
const CHAMADAS_POR_DIA_DIARIO = 'cerca de 200';

function formatarDataHora(iso: string | null): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const SincronizacaoComprasSection: React.FC = () => {
  const [estado, setEstado] = useState<EstadoDaSincronizacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [confirmarPausa, setConfirmarPausa] = useState(false);
  const [confirmarDiario, setConfirmarDiario] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setEstado(await fetchEstadoDaSincronizacao());
      setErro(false);
    } catch (error) {
      /*
       * 🔴 Estado seguro (`null`) E sinal, nunca um default otimista: exibir "Ativa" por causa de
       * uma falha de carga faria alguém concluir que o sync está no ar — que é exatamente o oposto
       * do que a pausa existe para comunicar (regra da at-60).
       */
      setEstado(null);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível verificar o estado da sincronização. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alternarPausa = useCallback(async (pausado: boolean) => {
    setSalvando(true);
    try {
      setEstado(await definirPausaDoSync(pausado));
      notify.success(
        pausado
          ? 'Sincronização pausada. Nenhuma requisição será enviada ao Nasajon até você retomar.'
          : 'Sincronização retomada, no ritmo que estava configurado.',
      );
    } catch {
      notify.error(
        pausado
          ? 'Não foi possível pausar. A sincronização continua ativa.'
          : 'Não foi possível retomar. A sincronização continua pausada.',
      );
    } finally {
      setSalvando(false);
    }
  }, []);

  const trocarModo = useCallback(async (modo: ModoSync) => {
    setSalvando(true);
    try {
      setEstado(await definirModoDoSync(modo));
      notify.success(
        modo === 'DIARIO'
          ? 'Ritmo alterado: as leituras passam a acontecer uma vez ao dia.'
          : 'Ritmo normal restabelecido: as leituras voltam a acontecer várias vezes ao dia.',
      );
    } catch {
      notify.error('Não foi possível alterar o ritmo das leituras. Nada foi mudado.');
    } finally {
      setSalvando(false);
    }
  }, []);

  const pausadoEm = formatarDataHora(estado?.pausadoEm ?? null);
  const modoEm = formatarDataHora(estado?.modoEm ?? null);
  const hora = estado?.horaDaLeituraDiaria;

  return (
    <section className="admin-card">
      <h2 className="admin-card-title">Sincronização com o Nasajon</h2>

      <p className="admin-hint">
        O painel de Compras não consulta o Nasajon quando você abre a tela: ele lê uma cópia local,
        que o sistema atualiza sozinho ao longo do dia. Os controles abaixo decidem{' '}
        <strong>se</strong> e <strong>com que frequência</strong> essas leituras acontecem.
      </p>
      {/* transversal/at-99: a pausa é por sistema (decisão do Victor, 26/10/2026). */}
      <p className="admin-hint">
        Esta pausa vale para o Dashboard de Compras. O módulo de Requisições tem a sua, em
        Administração do Sistema → Requisições: para parar todas as chamadas ao Nasajon, pause nas
        duas.
      </p>

      {erro && (
        <p className="erro-de-tela">
          Não foi possível verificar o estado da sincronização. Isto não significa que ela esteja
          ativa nem pausada — significa que não conseguimos perguntar.
        </p>
      )}

      {carregando && <p className="admin-hint">Carregando...</p>}

      {estado && (
        <>
          <div className="admin-input-group">
            <span
              className={`cilia-badge ${estado.pausado ? 'badge-cinza' : 'badge-verde'}`}
            >
              {estado.pausado ? 'Pausada' : 'Ativa'}
            </span>
            <span className="admin-texto-secundario">
              {estado.pausado
                ? `Pausada${pausadoEm ? ` em ${pausadoEm}` : ''}${
                    estado.pausadoPor ? ` por ${estado.pausadoPor.nome}` : ''
                  }.`
                : pausadoEm
                  ? `Retomada${pausadoEm ? ` em ${pausadoEm}` : ''}${
                      estado.pausadoPor ? ` por ${estado.pausadoPor.nome}` : ''
                    }.`
                  : 'Nunca foi pausada neste sistema.'}
            </span>
            {/*
              As classes vêm do `adminSistema.css`, que esta seção importa — e não das `filtro-btn-*`
              que o `AtivacaoDosRadaresSection` usa: aquelas vivem no CSS de um modal de outra tela,
              e dependem do bundle tê-lo carregado (o acoplamento invisível do `BUG-V74`).
            */}
            <button
              type="button"
              className={`admin-btn ${estado.pausado ? 'admin-btn-primary' : 'admin-btn-danger-outline'}`}
              disabled={salvando}
              onClick={() => {
                // Pausar descreve o efeito antes de executar (padrão at-18/at-46). Retomar não
                // precisa: o efeito é o sistema voltar ao que se espera dele.
                if (estado.pausado) void alternarPausa(false);
                else setConfirmarPausa(true);
              }}
            >
              {salvando ? 'Salvando...' : estado.pausado ? 'Retomar' : 'Pausar'}
            </button>
          </div>

          <h3 className="admin-card-title" style={{ marginTop: 20 }}>
            Ritmo das leituras
          </h3>
          <p className="admin-hint">
            No ritmo <strong>normal</strong>, as partes do painel são lidas em frequências
            diferentes ao longo do expediente, e uma mudança no Nasajon aparece aqui em até 3
            minutos — são {CHAMADAS_POR_DIA_NORMAL} consultas por dia. No ritmo{' '}
            <strong>diário</strong>, tudo é lido de uma vez só no fim do dia
            {hora ? `, às ${hora}h` : ''} — {CHAMADAS_POR_DIA_DIARIO} consultas por dia, e o painel
            deixa de mudar durante o expediente.
          </p>

          <div className="admin-input-group">
            <select
              className="admin-select"
              value={estado.modo}
              disabled={salvando}
              onChange={(e) => {
                const escolhido = e.target.value as ModoSync;
                if (escolhido === estado.modo) return;
                // Trocar para diário tira do sistema uma propriedade que as pessoas usam (o "quase
                // tempo real"); voltar ao normal só devolve o que se espera.
                if (escolhido === 'DIARIO') setConfirmarDiario(true);
                else void trocarModo('NORMAL');
              }}
            >
              <option value="NORMAL">Normal - várias leituras por dia, em horário comercial</option>
              <option value="DIARIO">
                {hora ? `Uma vez ao dia, às ${hora}h` : 'Uma vez ao dia, no fim do dia'}
              </option>
            </select>
            <span className="admin-texto-secundario">
              {modoEm
                ? `Alterado em ${modoEm}${estado.modoPor ? ` por ${estado.modoPor.nome}` : ''}.`
                : 'Nunca foi alterado neste sistema.'}
            </span>
          </div>

          {estado.pausado && estado.modo === 'DIARIO' && (
            <p className="aviso-de-tela">
              O ritmo diário fica guardado e volta a valer assim que a sincronização for retomada.
            </p>
          )}
        </>
      )}

      {confirmarPausa && (
        <ConfirmModal
          isOpen
          title="Pausar a sincronização com o Nasajon?"
          message={
            'Nenhuma requisição será enviada ao Nasajon enquanto durar a pausa - nem nos horários ' +
            'automáticos, nem pelo botão de sincronizar. O painel de Compras continua abrindo, com ' +
            'os dados que já foram recebidos, e avisa que está pausado. Nada é apagado, e o ritmo ' +
            'configurado volta a valer quando você retomar.'
          }
          confirmLabel="Pausar"
          onConfirm={() => {
            setConfirmarPausa(false);
            void alternarPausa(true);
          }}
          onCancel={() => setConfirmarPausa(false)}
        />
      )}

      {confirmarDiario && (
        <ConfirmModal
          isOpen
          title="Passar para uma leitura por dia?"
          message={
            `As leituras deixam de acontecer ao longo do expediente e passam a acontecer uma vez ` +
            `só${hora ? `, às ${hora}h` : ' no fim do dia'}. Durante o dia o painel não muda: uma ` +
            `requisição criada de manhã no Nasajon só aparece aqui à noite. O consumo cai de ` +
            `${CHAMADAS_POR_DIA_NORMAL} para ${CHAMADAS_POR_DIA_DIARIO} consultas por dia.`
          }
          confirmLabel="Usar uma leitura por dia"
          onConfirm={() => {
            setConfirmarDiario(false);
            void trocarModo('DIARIO');
          }}
          onCancel={() => setConfirmarDiario(false)}
        />
      )}
    </section>
  );
};

export default SincronizacaoComprasSection;
